/** Helpers de presentación de la vista "Mi día". */

const fmt = (n, dec = 0) => (n == null ? null : Number(n).toLocaleString('es-ES', {
  maximumFractionDigits: dec, minimumFractionDigits: 0,
}));

function horasMin(min) {
  if (min == null) return null;
  return `${Math.floor(min / 60)} h ${String(Math.round(min % 60)).padStart(2, '0')} min`;
}

function metricas(lista) {
  return lista.filter(([, valor]) => valor != null).map(([label, valor]) => ({ label, valor }));
}

export function prioridadClase(prioridad) {
  if (prioridad >= 5) return 'text-danger';
  if (prioridad >= 3) return 'text-warning';
  if (prioridad >= 1) return 'text-primary';
  return 'text-muted';
}

/** Una tarjeta por fuente que tenga datos de hoy; las demás no aparecen. */
export function tarjetasSaludHoy(salud) {
  if (!salud) return [];
  const out = [];
  const { oura, fitdays, coros, trainingpeaks } = salud;

  if (oura) {
    out.push({
      clave: 'oura',
      titulo: 'Oura',
      metricas: metricas([
        ['Readiness', fmt(oura.readiness)],
        ['Sueño', fmt(oura.sueno)],
        ['Tiempo dormido', horasMin(oura.minutosSueno)],
        ['Actividad', fmt(oura.actividad)],
        ['Pasos', fmt(oura.pasos)],
      ]),
    });
  }
  if (fitdays) {
    out.push({
      clave: 'fitdays',
      titulo: 'FitDays',
      metricas: metricas([
        ['Peso', fitdays.pesoKg != null ? `${fmt(fitdays.pesoKg, 1)} kg` : null],
        ['Grasa', fitdays.grasaPct != null ? `${fmt(fitdays.grasaPct, 1)} %` : null],
        ['IMC', fmt(fitdays.imc, 1)],
        ['Agua', fitdays.aguaPct != null ? `${fmt(fitdays.aguaPct, 1)} %` : null],
      ]),
    });
  }
  if (trainingpeaks) {
    out.push({
      clave: 'trainingpeaks',
      titulo: 'TrainingPeaks',
      metricas: [],
      lineas: trainingpeaks.entrenos.map((e) => {
        const meta = [e.minutos ? `${e.minutos} min` : null, e.km ? `${fmt(e.km, 1)} km` : null].filter(Boolean).join(' · ');
        return `${e.completado ? '✅' : '⏳'} ${e.titulo}${meta ? ` (${meta})` : ''}`;
      }),
    });
  }
  if (coros) {
    out.push({
      clave: 'coros',
      titulo: 'Coros',
      metricas: metricas([
        ['VFC', fmt(coros.hrv)],
        ['FC reposo', coros.fcReposo != null ? `${fmt(coros.fcReposo)} ppm` : null],
        ['Carga', fmt(coros.cargaEntreno)],
        ['VO2max', fmt(coros.vo2max)],
        ['Sueño', horasMin(coros.minutosSueno)],
        ['Puntuación sueño', fmt(coros.puntuacionSueno)],
      ]),
    });
  }
  return out;
}

/** Nota para el hueco de Oura cuando no hay datos de hoy (null si no hay nada que explicar). */
export function notaOuraSinDatos(salud) {
  if (!salud || salud.oura) return null;
  const e = salud.ouraEstado;
  if (!e) return null;
  if (!e.conectado) return 'Oura no está conectado.';
  const ultimo = e.ultimoDia
    ? new Date(`${e.ultimoDia}T12:00:00`).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })
    : null;
  return `Oura: aún sin datos de hoy${ultimo ? ` (último dato: ${ultimo})` : ''}. Oura los publica al sincronizar el anillo.`;
}

const RE_ENLACE = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|(https?:\/\/[^\s)]+)/g;

/**
 * Parte un texto en trozos { texto, url? } para pintar enlaces sin v-html.
 * Entiende [texto](url) y URLs sueltas.
 */
export function trocearTexto(texto) {
  const s = String(texto || '');
  const trozos = [];
  let desde = 0;
  for (const m of s.matchAll(RE_ENLACE)) {
    if (m.index > desde) trozos.push({ texto: s.slice(desde, m.index) });
    const url = m[2] || m[3];
    trozos.push({ texto: m[1] || dominioDe(url), url });
    desde = m.index + m[0].length;
  }
  if (desde < s.length) trozos.push({ texto: s.slice(desde) });
  return trozos.length ? trozos : [{ texto: s }];
}

function dominioDe(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

export function saludo(fecha = new Date()) {
  const h = Number(new Intl.DateTimeFormat('es-ES', { hour: 'numeric', hour12: false, timeZone: 'Europe/Madrid' }).format(fecha));
  if (h < 6) return 'Buenas noches';
  if (h < 13) return 'Buenos días';
  if (h < 21) return 'Buenas tardes';
  return 'Buenas noches';
}

const CLAVE_CACHE_TICK = 'miDia:ticktick:v1';

/**
 * Las cachés del navegador son por usuario: en un dispositivo compartido, quien entra después
 * no debe ver ni un instante los datos del anterior.
 */
export function claveDeUsuario(base) {
  try {
    const u = JSON.parse(localStorage.getItem('user') || 'null');
    return u && u.id != null ? `${base}:u${u.id}` : `${base}:anon`;
  } catch {
    return `${base}:anon`;
  }
}

function hoyEnMadrid() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Madrid' }).format(new Date());
}

/**
 * Última respuesta de TickTick guardada en el navegador, para pintar Mi día al instante.
 * Si es de otro día solo se aprovechan las tareas (las atrasadas siguen valiendo);
 * hábitos y eventos son del día y se marcan como "cargando" hasta que llegue lo fresco.
 */
export function leerCacheTick() {
  try {
    const c = JSON.parse(localStorage.getItem(claveDeUsuario(CLAVE_CACHE_TICK)) || 'null');
    if (!c || !c.data) return null;
    if (c.dia === hoyEnMadrid()) return c.data;
    return { ...c.data, habitos: [], eventos: [], habitosCargando: true };
  } catch {
    return null;
  }
}

export function guardarCacheTick(data) {
  if (data.habitosCargando) return; // respuesta parcial (solo tareas): no sustituye a una copia completa
  try {
    const limpio = { ...data };
    delete limpio.obsoleto;
    delete limpio.habitosCargando;
    localStorage.setItem(claveDeUsuario(CLAVE_CACHE_TICK), JSON.stringify({ dia: data.dia, data: limpio }));
  } catch { /* sin almacenamiento: se ignora */ }
}

/**
 * Caché genérica en el navegador (stale-while-revalidate): se pinta al instante lo último
 * que se vio y se refresca en segundo plano. Falla en silencio si no hay almacenamiento.
 */
export function leerCacheLocal(clave) {
  try {
    return JSON.parse(localStorage.getItem(claveDeUsuario(clave)) || 'null');
  } catch {
    return null;
  }
}

export function guardarCacheLocal(clave, valor) {
  try {
    localStorage.setItem(claveDeUsuario(clave), JSON.stringify(valor));
  } catch { /* sin almacenamiento: se ignora */ }
}

export const CLAVE_CACHE_DATOS = 'miDia:datos:v1';
export const CLAVE_CACHE_AGENDA = 'miDia:agenda:v1';
export const hoyMadrid = hoyEnMadrid;

/** Calienta la caché del servidor y la local nada más abrir la app, antes de entrar en Mi día. */
export async function precargarTick(axios) {
  try {
    const { data } = await axios.get('/api/mi-dia/ticktick');
    if (data && data.configured && !data.errores?.length) guardarCacheTick(data);
  } catch { /* es solo una precarga */ }
}

